import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, isEmpty, result } from 'lodash';
import moment from 'moment';
import {
  genarateTableConfigs,
  generateCsvConfigs,
  generateCsvData,
  generateGroupedTableConfigs,
  getGlobalSettings
} from '../utils';
import {
  DownloadCSVWrapper,
  TransactionDetalisPanel,
  TransactionDetalisPanelHeader,
  TransactionDetalisPanelTitle,
  TransactionDetailsTable
} from './styled';
import ComponentsMap from '../ComponentsMap';
import { DEFAULT_MAP_NAMESPACE } from './constants';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE
} from '../../constants/commonConstants';
import DownloadCSV from '../../../components/DownloadCSV';
import { convertDateInputToEpoch } from '../../../util/timezone';
import TransactionDetailsFilters from './TransactionDetailsFilters';

const TransactionDetails = ({
  apiActions,
  alertId,
  isLoading,
  isCustomerInfoLoading,
  transactionHistory,
  transactionHistoryDownloaded,
  settings,
  gsQuery,
  typologies,
  customerId,
  mapNamespace = DEFAULT_MAP_NAMESPACE,
  title,
  hasDownloadButton = true,
  hasTriggeredOnlySwitch = true,
  requireTriggeredOnlyParam,
  showWidgetActionLabels = true,
  className,
  csvHeaders,
  alertDetails = {},
  hasTypologiesFilter = true,
  manualCaseDate,
  isSmartAssessment,
  transactionTriggeredVariants,
  isPrevention
}) => {
  const { data: alertInfo } = alertDetails;
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState({
    isTriggeredOnly: true,
    typologyId: 'all',
    duration: 30
  });

  const alertDateTO = useMemo(
    () =>
      manualCaseDate
        ? moment(new Date(manualCaseDate), 'DD/MM/YYYY')
        : alertInfo?.alertDate
        ? moment(new Date(alertInfo.alertDate), 'DD/MM/YYYY')
        : moment(),
    [manualCaseDate, alertInfo]
  );
  const alertDateFROM = useMemo(
    () =>
      cloneDeep(alertDateTO)
        .subtract(query.duration ?? 30, 'days')
        .add(1, 'days'),
    [alertDateTO]
  );

  const {
    getAMLSCommonAlertPartyIdentity,
    getAMLSTMCommonTransactionHistory,
    getAMLSTMCommonTransactionTriggeredTypologies,
    getAMLSTMCommonTransactionTriggeredVariants,
    downloadAMLSTMCommonTransactionHistory
  } = apiActions;
  const {
    widgetConfig,
    isSingleLevel = false,
    isVisible
  } = getGlobalSettings({ settings, gsQuery }) || {};

  useEffect(() => {
    //The "To" date should not greater than alert Date.
    //So compare the "To" with "Alert Date" when date range only selected(without duration)
    if (query.from && query.to && !query.duration) {
      if (alertDateTO.endOf('day').isBefore(query.to.endOf('day'))) {
        setQuery((value) => ({
          ...value,
          from: alertDateFROM,
          to: alertDateTO,
          duration: 30
        }));
      }
    } else {
      setQuery((value) => ({
        ...value,
        from: alertDateFROM,
        to: alertDateTO,
        duration: value.duration
      }));
    }
  }, [alertDateTO]);

  // useEffect(() => {
  //   if (!customerId && !isCustomerInfoLoading) {
  //     getAMLSCommonAlertPartyIdentity({ alertId });
  //   }
  // }, []);

  useEffect(() => {
    // Set the typologyId as 'all' when the alert doesn't contain the selected typology while switching alert.
    if (query?.typologyId !== 'all') {
      if (
        (!isPrevention &&
          typologies?.length &&
          !typologies?.some(({ id }) => id === query?.typologyId)) ||
        (transactionTriggeredVariants?.length &&
          !transactionTriggeredVariants?.some((id) => id === query?.typologyId))
      ) {
        setQuery((value) => ({
          ...value,
          typologyId: 'all'
        }));
      }
    }
  }, [typologies, transactionTriggeredVariants, query?.typologyId]);

  useEffect(() => {
    const { from, to, typologyId, isTriggeredOnly } = query;
    if (
      isVisible &&
      from &&
      (manualCaseDate || alertId === alertInfo?.alertId)
    ) {
      const triggeredOnly = hasTriggeredOnlySwitch
        ? isTriggeredOnly
        : undefined;

      getAMLSTMCommonTransactionHistory({
        customerId,
        alertId,
        limit: currentPageSize,
        offset: (DEFAULT_CURRENT_PAGE - 1) * currentPageSize,
        minAmount: 0,
        maxAmount: '000',
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly: triggeredOnly || requireTriggeredOnlyParam,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        isSmartAssessment
      });
    }
  }, [customerId, query, isVisible, alertInfo]);

  useEffect(() => {
    if (hasTypologiesFilter && alertId) {
      if (isPrevention) {
        getAMLSTMCommonTransactionTriggeredVariants({ alertId });
      } else {
        getAMLSTMCommonTransactionTriggeredTypologies({ alertId });
      }
    }
  }, [alertId, hasTypologiesFilter]);

  const onChangeWidgetAction = (values) => {
    const triggeredOnly = hasTriggeredOnlySwitch
      ? values.isTriggeredOnly
      : undefined;

    setQuery({
      ...values,
      isTriggeredOnly: triggeredOnly || requireTriggeredOnlyParam
    });
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  const onDownloadClick = () => {
    if (!isEmpty(alertId) && isVisible) {
      const { from, to, typologyId, isTriggeredOnly } = query;

      downloadAMLSTMCommonTransactionHistory({
        customerId,
        alertId,
        minAmount: 0,
        maxAmount: '000',
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        isSmartAssessment
      });
    }
  };

  const onPaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
    const { from, to, isTriggeredOnly, typologyId } = query;
    if (from && to && !isEmpty(alertId) && isVisible) {
      getAMLSTMCommonTransactionHistory({
        customerId,
        alertId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        minAmount: 0,
        maxAmount: '000',
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly,
        typologyId: typologyId === 'all' ? undefined : typologyId,
        isSmartAssessment
      });
    }
  };

  const getFileName = () => {
    const dateObj = new Date();
    const timestamp = dateObj.getTime();
    return `Transactions__${customerId}--${query?.from?.format(
      'DDMMYYYY'
    )}-to-${query?.to?.format('DDMMYYYY')}__${timestamp}.csv`;
  };

  const typologyVariants = useMemo(
    () =>
      transactionTriggeredVariants
        ?.filter((t, i) => transactionTriggeredVariants.indexOf(t) === i)
        ?.map((d) => ({
          id: d,
          name: d
        })),
    [transactionTriggeredVariants]
  );

  return (
    <TransactionDetalisPanel className={className}>
      {console.log('widgetConfig', widgetConfig)}
      {hasDownloadButton && (
        <DownloadCSVWrapper>
          <DownloadCSV
            csvData={generateCsvData(
              transactionHistoryDownloaded?.payload || []
            )}
            csvHeaders={
              csvHeaders ||
              generateCsvConfigs({
                settings: widgetConfig
              })
            }
            fileName={getFileName()}
            onDownloadClick={onDownloadClick}
            disabled={(transactionHistory?.payload || []).length === 0}
          />
        </DownloadCSVWrapper>
      )}
      <TransactionDetalisPanelHeader>
        {title && (
          <TransactionDetalisPanelTitle>{title}</TransactionDetalisPanelTitle>
        )}
        <TransactionDetailsFilters
          hasTriggeredOnlySwitch={hasTriggeredOnlySwitch}
          hasTypologiesFilter={hasTypologiesFilter}
          typologies={isPrevention ? typologyVariants : typologies || []}
          showLabels={showWidgetActionLabels}
          filters={query}
          onFilterChange={onChangeWidgetAction}
          alertDateTO={alertDateTO}
        />
      </TransactionDetalisPanelHeader>
      <TransactionDetailsTable
        dataSource={transactionHistory?.payload || []}
        columns={
          isSingleLevel
            ? genarateTableConfigs({
                settings: widgetConfig,
                configs: result(ComponentsMap, mapNamespace) || {}
              })
            : generateGroupedTableConfigs({
                settings: widgetConfig,
                configs: result(ComponentsMap, mapNamespace) || {}
              })
        }
        loading={isLoading}
        pagination={{
          total: transactionHistory?.total || 0,
          current: currentPage,
          onChange: onPaginationChange,
          pageSize: currentPageSize,
          showTotal: (total) => `${total} Records`,
          showSizeChanger: false
        }}
      />
    </TransactionDetalisPanel>
  );
};

TransactionDetails.propTypes = {
  apiActions: PropTypes.object.isRequired,
  alertId: PropTypes.string,
  transactionHistory: PropTypes.object,
  transactionHistoryDownloaded: PropTypes.object,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
  isCustomerInfoLoading: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  gsQuery: PropTypes.object.isRequired,
  typologies: PropTypes.array,
  customerId: PropTypes.string.isRequired,
  mapNamespace: PropTypes.string,
  hasDownloadButton: PropTypes.bool,
  title: PropTypes.string,
  hasTriggeredOnlySwitch: PropTypes.bool,
  requireTriggeredOnlyParam: PropTypes.bool,
  showWidgetActionLabels: PropTypes.bool,
  className: PropTypes.string,
  csvHeaders: PropTypes.object,
  hasTypologiesFilter: PropTypes.bool,
  alertDetails: PropTypes.object,
  manualCaseDate: PropTypes.string,
  isPrevention: PropTypes.bool
};

export default TransactionDetails;
